'use client';

import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { Selection } from 'react-aria-components';

import ChevronIcon from '@/fo/icons/base/icons/chevron24.svg';
import SearchIcon from '@/fo/icons/base/icons/search.svg';
import { UiKitInputSearch } from '@/fo/ui/kit/input/search';
import { UiKitListboxBase } from '@/fo/ui/kit/listbox/base';

import { DEFAULT_SORT_OPTION, SortOption, SortOptionEnum } from '../../types';

import { styles } from './ListPanel.styles';

export interface ListPanelProps {
  onSortChange?: (value: SortOptionEnum) => void;
  sort?: SortOptionEnum;
  onSearch?: (value: string) => void;
  initialQuery?: string;
}

export function ListPanel({
  onSortChange,
  sort,
  onSearch,
  initialQuery
}: ListPanelProps) {
  const slots = styles();

  const t = useTranslations('article');

  const [sortValue, setSort] = useState<SortOptionEnum>(
    sort ?? DEFAULT_SORT_OPTION
  );

  const [open, setOpen] = useState(false);

  const sortOptions: SortOption[] = [
    {
      label: t('sort.newest'),
      value: SortOptionEnum.Newest
    },
    {
      label: t('sort.popular'),
      value: SortOptionEnum.Popular
    },
    {
      label: t('sort.withNaos'),
      value: SortOptionEnum.WithNaos
    }
  ];

  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleSortChange = (value: Selection) => {
    if (value === 'all') {
      return;
    }

    const selectedOption = Array.from(value)[0];

    onSortChange?.(selectedOption as SortOptionEnum);
    setSort(selectedOption as SortOptionEnum);
    setOpen(false);
  };

  return (
    <div className={slots.container()}>
      <div className={slots.base()}>
        <div className={slots.search()}>
          <UiKitInputSearch
            placeholder={t('search.placeholder')}
            label={t('search.placeholder')}
            startAdornment={<SearchIcon />}
            onChange={(e) => onSearch?.(e.target.value)}
            defaultValue={initialQuery}
          />
        </div>
        <div className={slots.sort()}>
          <button
            ref={triggerRef}
            className={slots.sortButton()}
            onClick={() => setOpen(!open)}
          >
            {sortOptions.find((option) => option.value === sortValue)?.label}
            <ChevronIcon className={slots.sortButtonIcon({ open })} />
          </button>
          <UiKitListboxBase
            isOpen={open}
            triggerRef={triggerRef}
            placement="bottom right"
            offset={4}
            crossOffset={12}
            options={sortOptions}
            onSelectionChange={handleSortChange}
            width="225px"
            onOpenChange={setOpen}
          />
        </div>
      </div>
    </div>
  );
}
